import type { RxJsonSchema } from 'rxdb'

/**
 * Page Document Type
 * Represents a rich content page (like Notion) within a project.
 * Supports unlimited nesting via parentPageId.
 */
export interface PageDocument {
  id: string
  envId: string
  projectId: string
  parentPageId: string | null
  title: string
  slug: string
  icon: string | null
  iconType: string | null
  iconColor: string | null
  sortOrder: string | null
  coverImageUrl: string | null
  coverImageType: string | null
  coverImageMeta: string | null
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Page
 */
export const pageSchema: RxJsonSchema<PageDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    projectId: {
      type: 'string',
      maxLength: 36,
    },
    parentPageId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    title: {
      type: 'string',
      maxLength: 500,
    },
    slug: {
      type: 'string',
      maxLength: 50,
    },
    icon: {
      type: ['string', 'null'],
      maxLength: 50,
    },
    iconType: {
      type: ['string', 'null'],
      maxLength: 10,
    },
    iconColor: {
      type: ['string', 'null'],
      maxLength: 7,
    },
    sortOrder: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    coverImageUrl: {
      type: ['string', 'null'],
      maxLength: 2048,
    },
    coverImageType: {
      type: ['string', 'null'],
      maxLength: 20,
    },
    coverImageMeta: {
      type: ['string', 'null'],
      maxLength: 2048,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'projectId',
    'title',
    'slug',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'updatedOn',
    ['envId', 'isDeleted'],
    ['envId', 'projectId'],
    ['envId', 'projectId', 'isDeleted'],
  ],
}
